import { BlockNoteEditor } from '@blocknote/core';
import { BlockSchema } from '../schema';
import getBlockNoteImageUploadError from './get-block-note-image-upload-error';
import getEditorDomSearchRoot from './get-editor-dom-search-root';

const FILE_DATA_TRANSFER_TYPE = 'Files';

export default function registerRejectUnsupportedImageFileInsert(
  editor: BlockNoteEditor<BlockSchema>,
  onError?: (message: string) => void,
) {
  const rejectIfUnsupported = (event: ClipboardEvent | DragEvent) => {
    if (!isEventForEditor(editor, event)) {
      return;
    }

    const dataTransfer =
      'dataTransfer' in event ? event.dataTransfer : event.clipboardData;
    if (!dataTransfer || !isFileDataTransfer(dataTransfer)) {
      return;
    }

    const files = Array.from(dataTransfer.files);
    if (files.length === 0) {
      return;
    }

    const message = findUnsupportedImageUploadError(files);
    if (!message) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    if (onError) {
      window.setTimeout(() => {
        onError(message);
      }, 0);
    }
  };

  document.addEventListener('paste', rejectIfUnsupported, true);
  document.addEventListener('drop', rejectIfUnsupported, true);

  return () => {
    document.removeEventListener('paste', rejectIfUnsupported, true);
    document.removeEventListener('drop', rejectIfUnsupported, true);
  };
}

function isEventForEditor(
  editor: BlockNoteEditor<BlockSchema>,
  event: ClipboardEvent | DragEvent,
) {
  const root = getEditorDomSearchRoot(editor) ?? editor.domElement;
  if (!root) {
    return false;
  }

  if (event.type === 'paste') {
    const activeElement = document.activeElement;
    return activeElement instanceof Node && root.contains(activeElement);
  }

  const target = event.target;
  return target instanceof Node && root.contains(target);
}

function isFileDataTransfer(dataTransfer: DataTransfer) {
  return (
    dataTransfer.files.length > 0 ||
    Array.from(dataTransfer.types).includes(FILE_DATA_TRANSFER_TYPE)
  );
}

function findUnsupportedImageUploadError(files: Array<File>) {
  for (const file of files) {
    const message = getBlockNoteImageUploadError(file);
    if (message) {
      return message;
    }
  }
  return null;
}
